import { useState, type ReactNode } from "react";
import { Link } from "@inertiajs/react";
import { Spinner } from "@/components/ui/Spinner";
import { Dropdown } from "@/components/ui/Dropdown";
import { Download } from "@/components/ui/icons";
import { useEnrichmentRun } from "@/hooks/useEnrichmentRun";
import {
  isEnrichmentRunActive,
  isEnrichmentRunStopping,
  type EnrichmentRun,
} from "@/types/reading";
import { cn } from "@/utils/cn";
import { filledFill, focusRing, iconFill, quietFill } from '@/components/ui/buttonStyles'

interface FetchLinguisticDataButtonProps {
  readingId: number;
  /** False when the reading's language has no lookup support (FR-020). */
  supported: boolean;
}

/**
 * Admin-only action that pre-warms a reading's vocabulary in the background, so words
 * a learner has not clicked yet resolve instantly instead of waiting on a slow
 * external lookup.
 *
 * On a reading page this is housekeeping, not the point of the page, so it occupies a
 * single toolbar circle beside "Aa" and keeps its status behind it. Only what the
 * admin standing on this page needs is shown — one line of state and one action. The
 * per-outcome breakdown and the history across every reading belong to the admin
 * panel's Enrichment Runs tab, which this panel links to.
 *
 * The one thing that must be legible without opening it is "something is running":
 * the trigger spins while a run is in flight.
 */
export function FetchLinguisticDataButton({
  readingId,
  supported,
}: FetchLinguisticDataButtonProps) {
  const [open, setOpen] = useState(false);
  const { run, loading, starting, error, start, cancel } = useEnrichmentRun(readingId, supported);

  if (!supported) return null;

  const active = isEnrichmentRunActive(run);
  const stopping = isEnrichmentRunStopping(run);
  const busy = starting || active;

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      align="right"
      trigger={
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={busy ? "Fetching linguistic data" : "Fetch linguistic data"}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-busy={busy}
          className={cn(iconFill, open && filledFill)}
        >
          {busy ? <Spinner className="h-4 w-4" /> : <Download size={16} />}
        </button>
      }
    >
      <div role="dialog" aria-label="Linguistic data" className="w-full p-5 pb-1 sm:w-72 sm:p-4 sm:pb-1">
        <p className="font-mono text-xs uppercase tracking-widest text-muted">
          Linguistic data
        </p>

        <p aria-live="polite" className="mt-2 text-sm font-light text-body">
          {error ?? statusLine(run, loading)}
        </p>

        <div className="mt-4">
          {active ? (
            <PanelButton onClick={() => void cancel()} disabled={stopping} fill={quietFill}>
              {stopping ? "Stopping…" : "Stop"}
            </PanelButton>
          ) : (
            <PanelButton onClick={() => void start()} disabled={starting || loading} fill={filledFill}>
              Fetch
            </PanelButton>
          )}
        </div>

        <Link
          href="/admin"
          className={cn(
            "inline-flex items-center min-h-11 rounded text-xs font-light text-muted",
            "transition-colors duration-200 hover:text-body",
            focusRing,
          )}
        >
          All runs in Admin
        </Link>
      </div>
    </Dropdown>
  );
}

interface PanelButtonProps {
  onClick: () => void;
  disabled: boolean;
  fill: string;
  children: ReactNode;
}

function PanelButton({ onClick, disabled, fill, children }: PanelButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full inline-flex items-center justify-center min-h-11 px-3 rounded-md text-sm font-normal",
        "transition-colors duration-200",
        fill,
        focusRing,
        "disabled:opacity-60 disabled:cursor-default",
      )}
    >
      {children}
    </button>
  );
}

/**
 * One line, in the vocabulary of someone standing on the reading page: has this text
 * been prepared, is it being prepared now, and did it work. Everything a run records
 * about *which* words went which way is the admin panel's to show.
 */
function statusLine(run: EnrichmentRun | null, loading: boolean): string {
  if (loading) return "Checking…";
  if (run === null) return "Not fetched yet.";

  switch (run.status) {
    case "queued":
      return "Waiting to start.";
    case "running":
      return `${run.processed_words} of ${run.total_words} words`;
    case "completed":
      return completedLine(run);
    case "cancelled":
      return `Stopped at ${run.processed_words} of ${run.total_words} words.`;
    case "failed":
      return run.error_message ?? "The run failed. You can start a new one.";
  }
}

/**
 * A run that enriches nothing is the normal result of running twice — a success, not
 * a blank. It says so rather than reporting "0 words fetched".
 */
function completedLine(run: EnrichmentRun): string {
  if (run.enriched_count === 0) return "Nothing new to fetch.";

  return `${run.enriched_count} words fetched${finishedSuffix(run)}`;
}

function finishedSuffix(run: EnrichmentRun): string {
  if (!run.finished_at) return ".";

  const finished = new Date(run.finished_at);
  return ` at ${finished.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`;
}
